import React, { useEffect, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faHeart, faEye, faPlay, faImage, faMapMarkerAlt, faCalendar, faTrash } from '@fortawesome/free-solid-svg-icons';
import { Row, Col, Button, Spinner, Card, Modal, Badge } from '@themesberg/react-bootstrap';
import { GetApiRequest, DeleteApiRequest } from "../services/api";
import { toast } from '../components/Toaster';

function contentStatusDisplay(c) {
  const status =
    c.status || (c.isActive === false ? 'REMOVED_LEGACY' : 'ACTIVE');
  const labels = {
    ACTIVE: 'Active (public)',
    PENDING: 'Pending review',
    REMOVED_BY_OWNER: 'Removed by author',
    REMOVED_BY_MODERATOR: 'Removed by moderator',
    BANNED: 'Banned',
    REMOVED_LEGACY: 'Hidden (legacy)',
    HIDDEN_PENDING_REVIEW: 'Auto-hidden (reports)',
  };
  const variant =
    status === 'ACTIVE'
      ? 'success'
      : status === 'BANNED'
        ? 'danger'
        : status === 'PENDING'
          ? 'info'
          : 'warning';
  return { status, label: labels[status] || status, variant };
}

const ContentDetailsPage = () => {
  const { id } = useParams();
  const history = useHistory();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [mediaType, setMediaType] = useState('image');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchContentDetails = async (contentId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await GetApiRequest(`content/${contentId}`);
      if (response.data?.success && response.data?.data) {
        const data = response.data.data;
        setContent(data);

        // Set initial media: prefer fileAssets / videoAsset (medium/high for display), fallback to legacy fileUrls/videoUrl
        const hasImages = data.fileAssets?.length > 0 || data.fileUrls?.length > 0;
        const hasVideo = !!data.videoAsset || !!data.videoUrl;
        if (hasImages) {
          const firstImageUrl =
            data.fileAssets?.[0]?.urls?.medium ||
            data.fileAssets?.[0]?.urls?.high ||
            data.fileAssets?.[0]?.urls?.original ||
            data.fileUrls?.[0];
          setSelectedMedia(firstImageUrl);
          setMediaType('image');
        } else if (hasVideo) {
          const videoPlayUrl =
            data.videoAsset?.urls?.high ||
            data.videoAsset?.urls?.medium ||
            data.videoAsset?.urls?.original ||
            data.videoUrl;
          setSelectedMedia(videoPlayUrl);
          setMediaType('video');
        }
      } else {
        setError('Failed to load content details');
      }
    } catch (err) {
      console.error('Failed to fetch content:', err);
      setError(err.response?.data?.message || 'Failed to load content');
      toast.error('Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) fetchContentDetails(id);
  }, [id]);

  const handleMediaSelect = (url, type) => {
    setSelectedMedia(url);
    setMediaType(type);
  };

  const handleDeletePost = async () => {
    if (!id) return;
    setDeleteLoading(true);
    try {
      await DeleteApiRequest(`content/${id}`);
      toast.success('Post removed from public view');
      setShowDeleteModal(false);
      history.push('/content');
    } catch (err) {
      console.error('Failed to delete post:', err);
      toast.error(err?.response?.data?.message || 'Failed to remove post');
    } finally {
      setDeleteLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <div style={{ textAlign: 'center' }}>
          <Spinner animation="border" variant="primary" style={{ width: '50px', height: '50px' }} />
          <p style={{ color: '#6b7280', marginTop: '16px' }}>Loading content...</p>
        </div>
      </div>
    );
  }

  if (error || !content) {
    return (
      <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
        <Button variant="outline-secondary" onClick={() => history.goBack()} style={{ marginBottom: '24px' }}>
          <FontAwesomeIcon icon={faArrowLeft} className="me-2" /> Back
        </Button>
        <div className="alert alert-danger">
          <h4>Error Loading Content</h4>
          <p className="mb-0">{error || 'Content not found'}</p>
        </div>
      </div>
    );
  }

  const statusUi = contentStatusDisplay(content);

  return (
    <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '32px' }}>
        <Button variant="outline-secondary" onClick={() => history.goBack()} style={{ marginBottom: '16px', borderRadius: '8px' }}>
          <FontAwesomeIcon icon={faArrowLeft} className="me-2" /> Back to Gallery
        </Button>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '12px' }}>
            <h1 style={{ fontSize: '2rem', fontWeight: '700', color: '#1a1a1a', margin: 0 }}>Content Details</h1>
            <Badge bg={statusUi.variant} text={statusUi.variant === 'warning' ? 'dark' : undefined} style={{ fontSize: '0.85rem' }}>
              {statusUi.label}
            </Badge>
          </div>
          <Button
            variant="outline-danger"
            style={{ borderRadius: '8px', fontWeight: 600 }}
            onClick={() => setShowDeleteModal(true)}
          >
            <FontAwesomeIcon icon={faTrash} className="me-2" />
            Remove from public view
          </Button>
        </div>
      </div>

      <Row>
        <Col xs={12} lg={8}>
          {/* Main Media Display */}
          {selectedMedia && (
            <Card style={{ border: 'none', borderRadius: '16px', overflow: 'hidden', marginBottom: '24px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
              {mediaType === 'video' ? (
                <video src={selectedMedia} controls style={{ width: '100%', height: 'auto', background: '#000', maxHeight: '600px' }} />
              ) : (
                <img src={selectedMedia} alt="content" style={{ width: '100%', height: 'auto', maxHeight: '600px', objectFit: 'contain', background: '#f9fafb' }} />
              )}
            </Card>
          )}

          {/* Gallery Thumbnails: fileAssets (medium for thumb + display) and videoAsset (thumbnail for thumb, high/medium for playback) */}
          {(() => {
            const imageItems = content.fileAssets?.length
              ? content.fileAssets.map((asset, idx) => ({
                  type: 'image',
                  displayUrl: asset.urls?.medium || asset.urls?.high,
                  thumbUrl: asset.urls?.medium || asset.urls?.thumbnail || asset.urls?.low,
                }))
              : (content.fileUrls || []).map((url) => ({ type: 'image', displayUrl: url, thumbUrl: url }));
            const videoItem = content.videoAsset
              ? {
                  type: 'video',
                  displayUrl:
                    content.videoAsset.urls?.high ||
                    content.videoAsset.urls?.medium ||
                    content.videoAsset.urls?.original,
                  thumbUrl:
                    content.videoAsset.thumbnail ||
                    content.videoAsset.urls?.thumbnail,
                }
              : content.videoUrl
                ? { type: 'video', displayUrl: content.videoUrl, thumbUrl: content.videoUrl }
                : null;
            const hasGallery = imageItems.length > 1 || (imageItems.length > 0 && videoItem) || (imageItems.length === 0 && videoItem);
            if (!hasGallery) return null;
            return (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '12px', marginBottom: '24px' }}>
                {imageItems.map((item, idx) => (
                  <div
                    key={`img-${idx}`}
                    onClick={() => handleMediaSelect(item.displayUrl, 'image')}
                    style={{
                      position: 'relative',
                      height: '120px',
                      borderRadius: '12px',
                      overflow: 'hidden',
                      cursor: 'pointer',
                      border: selectedMedia === item.displayUrl ? '3px solid #667eea' : '3px solid transparent',
                      transition: 'all 0.2s',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.05)')}
                    onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                  >
                    <img
                      src={item.thumbUrl}
                      alt={`gallery-${idx}`}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  </div>
                ))}
                {videoItem && (
                  <div
                    onClick={() => handleMediaSelect(videoItem.displayUrl, 'video')}
                    style={{
                      position: 'relative',
                      height: '120px',
                      borderRadius: '12px',
                      overflow: 'hidden',
                      cursor: 'pointer',
                      border: selectedMedia === videoItem.displayUrl ? '3px solid #667eea' : '3px solid transparent',
                      transition: 'all 0.2s',
                      background: '#000',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.05)')}
                    onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                  >
                    <img
                      src={videoItem.thumbUrl}
                      alt="video"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                    <div
                      style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        background: 'rgba(0,0,0,0.6)',
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <FontAwesomeIcon icon={faPlay} style={{ color: 'white', fontSize: '1rem', marginLeft: '2px' }} />
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Description Card */}
          <Card style={{ border: 'none', borderRadius: '12px', marginBottom: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <Card.Body style={{ padding: '24px' }}>
              <h5 style={{ fontSize: '1.2rem', fontWeight: '700', color: '#1a1a1a', marginBottom: '16px' }}>Description</h5>
              <p style={{ color: '#4b5563', lineHeight: '1.7', fontSize: '1rem', margin: 0 }}>
                {content.description}
              </p>
            </Card.Body>
          </Card>

          {/* Stats */}
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            <Card style={{ flex: '1', minWidth: '200px', border: 'none', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
              <Card.Body style={{ padding: '20px', display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div style={{ width: '50px', height: '50px', borderRadius: '12px', background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <FontAwesomeIcon icon={faHeart} style={{ color: 'white', fontSize: '1.5rem' }} />
                </div>
                <div>
                  <div style={{ fontSize: '0.85rem', color: '#9ca3af', marginBottom: '4px' }}>Likes</div>
                  <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1a1a1a' }}>{content.likeCount || 0}</div>
                </div>
              </Card.Body>
            </Card>

            <Card style={{ flex: '1', minWidth: '200px', border: 'none', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
              <Card.Body style={{ padding: '20px', display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div style={{ width: '50px', height: '50px', borderRadius: '12px', background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <FontAwesomeIcon icon={faEye} style={{ color: 'white', fontSize: '1.5rem' }} />
                </div>
                <div>
                  <div style={{ fontSize: '0.85rem', color: '#9ca3af', marginBottom: '4px' }}>Views</div>
                  <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1a1a1a' }}>{content.viewCount || 0}</div>
                </div>
              </Card.Body>
            </Card>
          </div>
        </Col>

        <Col xs={12} lg={4}>
          {/* Creator Info */}
          <Card style={{ border: 'none', borderRadius: '12px', marginBottom: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <Card.Body style={{ padding: '24px' }}>
              <h5 style={{ fontSize: '1.1rem', fontWeight: '700', color: '#1a1a1a', marginBottom: '20px' }}>Created By</h5>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '16px', background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)', borderRadius: '12px' }}>
                {content.createdBy?.avatar ? (
                  <img src={content.createdBy.avatar} alt="avatar" style={{ width: '60px', height: '60px', borderRadius: '50%', objectFit: 'cover' }} />
                ) : (
                  <div style={{ width: '60px', height: '60px', borderRadius: '50%', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '1.5rem', fontWeight: '700' }}>
                    {content.createdBy?.name?.charAt(0).toUpperCase() || 'U'}
                  </div>
                )}
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: '600', color: '#1a1a1a', marginBottom: '4px', fontSize: '1rem' }}>
                    {content.createdBy?.name || 'Unknown User'}
                  </div>
                  {content.createdBy?.email && (
                    <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>{content.createdBy.email}</div>
                  )}
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Tagged Experiences */}
          {content.taggedExperiences?.length > 0 && (
            <Card style={{ border: 'none', borderRadius: '12px', marginBottom: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
              <Card.Body style={{ padding: '24px' }}>
                <h5 style={{ fontSize: '1.1rem', fontWeight: '700', color: '#1a1a1a', marginBottom: '20px' }}>
                  Tagged Experiences ({content.taggedExperiences.length})
                </h5>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {content.taggedExperiences.map(exp => (
                    <div 
                      key={exp._id} 
                      onClick={() => history.push(`/experiences/${exp._id}`)}
                      style={{ 
                        padding: '16px', 
                        background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)', 
                        border: '1px solid rgba(102, 126, 234, 0.2)', 
                        borderRadius: '12px', 
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateX(4px)';
                        e.currentTarget.style.borderColor = '#667eea';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateX(0)';
                        e.currentTarget.style.borderColor = 'rgba(102, 126, 234, 0.2)';
                      }}
                    >
                      <div style={{ fontWeight: '600', color: '#667eea', marginBottom: '8px', fontSize: '0.95rem' }}>
                        {exp.title}
                      </div>
                      {exp.category && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.8rem', color: '#6b7280', marginBottom: '6px' }}>
                          <FontAwesomeIcon icon={faImage} />
                          <span>{exp.category}</span>
                        </div>
                      )}
                      {exp.location?.address && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.8rem', color: '#6b7280' }}>
                          <FontAwesomeIcon icon={faMapMarkerAlt} />
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {exp.location.address}
                          </span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </Card.Body>
            </Card>
          )}

          {content.moderationLog && content.moderationLog.length > 0 && (
            <Card style={{ border: 'none', borderRadius: '12px', marginBottom: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
              <Card.Body style={{ padding: '24px' }}>
                <h5 style={{ fontSize: '1.1rem', fontWeight: '700', color: '#1a1a1a', marginBottom: '16px' }}>Change history</h5>
                <ul className="list-unstyled mb-0" style={{ fontSize: '0.88rem' }}>
                  {[...content.moderationLog].reverse().map((entry, idx, arr) => (
                    <li key={idx} style={{ paddingBottom: '12px', marginBottom: '12px', borderBottom: idx < arr.length - 1 ? '1px solid #f3f4f6' : 'none' }}>
                      <div style={{ fontWeight: 600, color: '#374151' }}>
                        {entry.action}
                        {entry.fromStatus && entry.toStatus && (
                          <span style={{ fontWeight: 500, color: '#6b7280' }}> · {entry.fromStatus} → {entry.toStatus}</span>
                        )}
                      </div>
                      <div style={{ color: '#9ca3af', marginTop: '4px' }}>
                        {entry.at ? new Date(entry.at).toLocaleString() : ''}
                        {entry.actor?.name && ` · ${entry.actor.name}`}
                      </div>
                      {entry.note && (
                        <div style={{ color: '#4b5563', marginTop: '6px' }}>{entry.note}</div>
                      )}
                    </li>
                  ))}
                </ul>
              </Card.Body>
            </Card>
          )}

          {/* Metadata */}
          <Card style={{ border: 'none', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <Card.Body style={{ padding: '24px' }}>
              <h5 style={{ fontSize: '1.1rem', fontWeight: '700', color: '#1a1a1a', marginBottom: '20px' }}>Information</h5>
              <div>
                <div style={{ marginBottom: '16px', paddingBottom: '16px', borderBottom: '1px solid #f3f4f6' }}>
                  <div style={{ fontSize: '0.8rem', color: '#9ca3af', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Created
                  </div>
                  <div style={{ color: '#1a1a1a', fontWeight: '500', fontSize: '0.9rem' }}>
                    <FontAwesomeIcon icon={faCalendar} className="me-2" style={{ color: '#667eea' }} />
                    {new Date(content.createdAt).toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '0.8rem', color: '#9ca3af', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Last Updated
                  </div>
                  <div style={{ color: '#1a1a1a', fontWeight: '500', fontSize: '0.9rem' }}>
                    <FontAwesomeIcon icon={faCalendar} className="me-2" style={{ color: '#667eea' }} />
                    {new Date(content.updatedAt).toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Modal show={showDeleteModal} onHide={() => !deleteLoading && setShowDeleteModal(false)} centered>
        <Modal.Header closeButton={!deleteLoading}>
          <Modal.Title>Remove post from public view</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          The post will be hidden from feeds and profiles. The record and media remain in the database.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={() => setShowDeleteModal(false)} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeletePost} disabled={deleteLoading}>
            {deleteLoading ? 'Removing…' : 'Remove from public view'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ContentDetailsPage;